import Link from "@/components/LocalizedLink";
import { ArrowRight, Check } from "lucide-react";
import Layout from "@/components/Layout";
import Section from "@/components/Section";
import HeroBackground from "@/components/HeroBackground";
import PrimaryCtaLink from "@/components/PrimaryCtaLink";
import Eyebrow from "@/components/Eyebrow";
import FAQAccordion from "@/components/FAQAccordion";
import IndustryCard from "@/components/IndustryCard";
import ProcessStep from "@/components/ProcessStep";
import ProofBar from "@/components/ProofBar";
import ServiceFlow from "@/components/ServiceFlow";
import SEO from "@/components/SEO";
import {
  SITE_URL,
  getCtaLabel,
  implementationServices,
  localize,
  siteContent,
} from "@/content/site";
import { homeFaqs } from "@/content/home";
import { workPatterns } from "@/content/proofTrust";
import { useTranslation } from "@/i18n/useTranslation";
import { faqPageJsonLd, webPageJsonLd } from "@/seo/jsonLd";
import { usePageSeo } from "@/seo/usePageSeo";

const valueItems = [
  {
    code: "V/01",
    title: {
      en: "Reduce manual throughput bottlenecks",
      fr: "Réduire les goulots d’étranglement du traitement manuel",
    },
    description: {
      en: "Classify, route, draft, reconcile, and escalate work with review boundaries where judgment or customer impact matters.",
      fr: "Classer, acheminer, rédiger et rapprocher le travail, puis transmettre les exceptions, avec une validation humaine lorsque le jugement ou l’incidence sur la clientèle comptent.",
    },
    href: "/solutions/ai-agents-workflow-automation",
  },
  {
    code: "V/02",
    title: {
      en: "Turn messy documents into governed inputs",
      fr: "Transformer les documents complexes en entrées gouvernées",
    },
    description: {
      en: "Extract, validate, and review contracts, invoices, forms, support records, RFPs, and operational documents with traceable confidence and exception handling.",
      fr: "Extraire et valider les contrats, factures, formulaires, dossiers de soutien, appels d’offres et documents opérationnels, avec des seuils de confiance traçables et une gestion des exceptions.",
    },
    href: "/solutions/document-intelligence",
  },
  {
    code: "V/03",
    title: {
      en: "Improve recurring commercial decisions",
      fr: "Améliorer les décisions commerciales récurrentes",
    },
    description: {
      en: "Forecast, score, prioritize, recommend, and route decisions where data exists but ownership, thresholds, and feedback still live in meetings.",
      fr: "Prévoir, évaluer, prioriser, recommander et acheminer des cas lorsque les données existent, mais que la responsabilité, les seuils et la rétroaction demeurent confinés aux réunions.",
    },
    href: "/solutions/predictive-models-decision-science",
  },
  {
    code: "V/04",
    title: {
      en: "Build the data layer AI can trust",
      fr: "Construire la couche de données dont le système a besoin",
    },
    description: {
      en: "Create source-of-truth pipelines, semantic definitions, access rules, quality checks, and activation layers before automation scales bad inputs.",
      fr: "Créer des chaînes de traitement de référence, des définitions sémantiques, des règles d’accès, des contrôles de qualité et des couches d’activation avant d’amplifier de mauvaises données.",
    },
    href: "/services/data-platforms",
  },
];

const processSteps = [
  {
    title: { en: "Choose the workflow", fr: "Choisir le processus" },
    description: {
      en: "Confirm the visible cost, owner, data reality, operating constraints, and whether AI is the right mechanism at all.",
      fr: "Confirmer le coût visible, la personne responsable, la réalité des données, les contraintes opérationnelles et la pertinence réelle de l’IA.",
    },
  },
  {
    title: { en: "Prove the operating case", fr: "Prouver le cas opérationnel" },
    description: {
      en: "Baseline the current work, test representative cases, expose blockers, and define the smallest system and acceptance evidence worth funding.",
      fr: "Établir la mesure de référence, tester des cas représentatifs, exposer les obstacles et définir le plus petit système ainsi que les preuves d’acceptation à financer.",
    },
  },
  {
    title: { en: "Deploy with control", fr: "Déployer avec contrôle" },
    description: {
      en: "Integrate the data, workflow logic, AI behaviour, human review, fallback paths, permissions, and measurement as one production system.",
      fr: "Intégrer les données, la logique du processus, le comportement de l’IA, la validation humaine, les solutions de repli, les autorisations et la mesure dans un seul système de production.",
    },
    marker: "HUMAN /",
  },
  {
    title: { en: "Operate and earn expansion", fr: "Exploiter et mériter l’élargissement" },
    description: {
      en: "Monitor behaviour, exceptions, adoption, operating cost, and business impact. Expand authority or scope only after the evidence holds.",
      fr: "Surveiller le comportement, les exceptions, l’adoption, le coût d’exploitation et l’incidence d’affaires. Élargir l’autorité ou le périmètre seulement lorsque les preuves tiennent.",
    },
  },
];

const consoleMetrics = [
  { code: "M/01", en: "Workflow candidate", fr: "Processus à évaluer" },
  { code: "M/02", en: "Data readiness", fr: "Préparation des données" },
  { code: "M/03", en: "Review boundary", fr: "Limite de validation" },
  { code: "M/04", en: "ROI baseline", fr: "Mesure de référence" },
];

const trustItems = [
  { en: "Evaluation harnesses", fr: "Bancs d’évaluation" },
  { en: "Prompt and workflow standards", fr: "Normes pour les instructions et les processus" },
  { en: "Human-in-the-loop controls", fr: "Contrôles de validation humaine" },
  { en: "Escalation logic", fr: "Règles de traitement des exceptions" },
  { en: "Observability and monitoring", fr: "Observabilité et surveillance" },
  { en: "Data quality checks", fr: "Contrôles de qualité des données" },
  { en: "Security, privacy, and governance", fr: "Sécurité, confidentialité et gouvernance" },
  { en: "ROI and adoption measurement", fr: "Mesure du rendement et de l’adoption" },
];

const frCopy = {
  heroEyebrow: "Processus coûteux du commerce — du cadrage à l’exploitation",
  heroTitle: "Corrigez les processus coûteux qui ralentissent vos opérations de commerce.",
  heroSecondaryCta: "Voir des mandats réels",
  problemHeadline: "Le bon point de départ est un problème opérationnel dont le coût est déjà visible.",
  problemBody:
    "Vert AI intervient lorsqu’un processus important est lent, manuel ou incohérent, ou lorsqu’une décision récurrente reste difficile à exécuter malgré les données disponibles.",
  buildsHeadline: "Un processus repensé. Un système fiable. Un résultat mesurable.",
  buildsBody:
    "Vert AI relie le processus, les données, les règles, l’IA, les contrôles et la mesure dans un même système. Le résultat attendu est une capacité utilisée par l’équipe et exploitable avec confiance.",
  valueHeadline: "La valeur se voit dans le travail qui change.",
  valueBody:
    "Les bons cas d’usage sont opérationnels, mesurables et proches des décisions récurrentes. Nous choisissons les processus où l’IA peut améliorer la vitesse, la qualité, le coût ou la précision sans retirer le jugement humain là où il compte.",
  osHeadline: "Choisir. Prouver. Déployer. Exploiter.",
  osBody:
    "Chaque phase réduit une incertitude précise : la valeur du processus, la qualité des preuves, la fiabilité du déploiement ou la capacité de l’équipe à exploiter le système.",
  servicesHeadline: "Quatre parcours de mise en œuvre après une décision fondée sur des preuves.",
  processHeadline: "Un chemin court du diagnostic au système exploité.",
  industriesHeadline: "Conçu pour les entreprises où la qualité des processus compte.",
  industriesBody:
    "Les meilleurs mandats se trouvent dans les entreprises où les données, les exceptions et les décisions récurrentes ont un coût réel.",
  trustHeadline: "L’IA de production exige plus qu’un modèle.",
  trustBody:
    "Le système doit être observable, gouverné, mesurable et utilisable par les équipes qui en dépendent. Les personnes conservent l’autorité lorsque le jugement, le risque ou l’incidence sur la clientèle comptent.",
  founderHeadline: "Expérience de production dirigée par le fondateur.",
  founderBody:
    "Guillaume dirige le cadrage et les décisions techniques clés. Son expérience professionnelle comprend des systèmes d’IA, d’apprentissage automatique et de données pour des environnements comme Louis Vuitton, Canada Goose, Lowe’s et Home Hardware. Ces entreprises ne sont pas présentées comme des clientes de Vert AI.",
  finalHeadline: "Présentez le processus coûteux que votre équipe ne devrait plus subir.",
  finalBody:
    "Apportez un processus, un problème de données ou une décision récurrente. Nous examinerons la valeur, le risque, la responsabilité et le chemin vers la production, y compris la possibilité de ne pas construire pour l’instant.",
};

const Index = () => {
  const { locale } = useTranslation();
  const isFr = locale === "fr";
  const primaryCtaLabel = getCtaLabel(siteContent.ctas.primary, locale);
  const reviewOfferCtaLabel = getCtaLabel(siteContent.ctas.reviewOffer, locale);
  const productionSystemsCtaLabel = getCtaLabel(siteContent.ctas.productionSystems, locale);
  const secondaryCtaLabel = getCtaLabel(siteContent.ctas.secondary, locale);
  const proofCtaLabel = getCtaLabel(siteContent.ctas.proof, locale);
  const seo = usePageSeo("home");
  const services = implementationServices;
  const industries = siteContent.industries.slice(0, 5);
  const selectedEngagement = workPatterns[1];
  const faqItems = homeFaqs.map((item) => ({
    q: localize(item.q, locale),
    a: localize(item.a, locale),
  }));
  const heroSupportBullets = isFr
    ? [
        "Choisir le bon processus opérationnel avant de construire.",
        "Ne pas automatiser avant que le risque et la responsabilité soient clairs.",
        "Corriger la fondation de données requise pour une IA utile.",
        "Livrer avec évaluation, surveillance, sécurité et validation humaine.",
        "Mesurer l’adoption, le rendement et l’impact opérationnel.",
      ]
    : [
        "Choose the right workflow before building.",
        "Do not automate before risk and ownership are clear.",
        "Fix the data foundation required for useful AI.",
        "Ship with evaluation, monitoring, security review, and human review.",
        "Measure adoption, ROI, and operating impact.",
      ];
  const heroMobileSubhead = localize(siteContent.brand.longPositioning, locale);

  const heroFlowNodes = isFr
    ? [
        { label: "Processus", sub: "Choisir" },
        { label: "Données", sub: "Préparer" },
        { label: "Système", sub: "Construire" },
        { label: "Impact", sub: "Mesurer", emphasis: true },
      ]
    : [
        { label: "Workflow", sub: "Select" },
        { label: "Data", sub: "Ready" },
        { label: "System", sub: "Build" },
        { label: "Impact", sub: "Measure", emphasis: true },
      ];

  const proofItems = [
    {
      code: "01",
      label: isFr ? "Priorité" : "Primary focus",
      value: isFr ? "Opérations de commerce au Canada." : "Canadian commerce operations.",
    },
    {
      code: "02",
      label: isFr ? "Livraison" : "Delivery",
      value: localize(siteContent.proof.founderLedDelivery, locale),
    },
    {
      code: "03",
      label: isFr ? "Preuves" : "Evidence",
      value: isFr ? "Quatre mandats réels présentés de façon anonymisée." : "Four real engagements presented anonymously.",
    },
    {
      code: "04",
      label: isFr ? "Contrôle" : "Controls",
      value: isFr
        ? "Évaluation, surveillance, validation humaine, gouvernance et mesure de la valeur."
        : "Evaluation, monitoring, human review, governance, and value measurement.",
    },
  ];

  const problemFailures = isFr
    ? [
        {
          code: "01",
          label: "Le travail s’accumule",
          body: "Les équipes classent, rapprochent, recherchent, rédigent ou transfèrent le même type de travail à répétition entre plusieurs systèmes.",
        },
        {
          code: "02",
          label: "Les décisions restent lentes",
          body: "Les données existent, mais les seuils, les règles, la responsabilité et la prochaine action demeurent dans des fichiers ou des réunions.",
        },
        {
          code: "03",
          label: "Un pilote ne peut pas être exploité",
          body: "Le prototype fonctionne, mais les données réelles, les exceptions, la validation, l’intégration, l’adoption et la mesure n’ont pas été conçues.",
        },
      ]
    : [
        {
          code: "01",
          label: "Work keeps piling up",
          body: "Teams repeatedly classify, reconcile, search, draft, or hand off the same work across disconnected systems.",
        },
        {
          code: "02",
          label: "Decisions stay slow",
          body: "The data exists, but thresholds, policy, ownership, and the next action still live in spreadsheets or meetings.",
        },
        {
          code: "03",
          label: "A pilot cannot be operated",
          body: "The prototype works, but live data, exceptions, review, integration, adoption, and measurement were never designed.",
        },
      ];

  const methodChecks = isFr
    ? ["Processus mesurable", "Données utilisables", "Validation humaine définie", "Mesure de référence"]
    : ["Measurable workflow", "Usable data", "Human review boundary", "ROI baseline"];

  const goodFit = isFr
    ? [
        "Un processus ou une décision récurrente dont le coût est déjà visible.",
        "Une personne responsable du processus et de son adoption.",
        "Des données accessibles, même si leur qualité doit être améliorée.",
        "Un indicateur opérationnel que l’équipe accepte de mesurer avant le développement.",
      ]
    : [
        "A recurring workflow or decision with a visible operating cost.",
        "An accountable workflow owner who can support adoption.",
        "Accessible data, even when quality work is still required.",
        "An operating signal the team will baseline before the build.",
      ];
  const notFit = isFr
    ? [
        "Une démonstration sans propriétaire, intégration ou voie d’adoption.",
        "Un mandat défini uniquement par un outil ou un modèle à utiliser.",
        "Une attente de rendement garanti sans mesure de référence ni attribution défendable.",
        "Une automatisation à risque élevé sans autorité humaine ni mécanisme de traitement des exceptions.",
      ]
    : [
        "A demo with no owner, integration, or adoption path.",
        "A scope defined only by a preferred tool or model.",
        "A guaranteed-return expectation without a baseline or defensible attribution.",
        "High-risk automation with no human authority or escalation path.",
      ];

  const pageJsonLd = webPageJsonLd({
    url: SITE_URL,
    name: seo.title,
    description: seo.description,
  });
  const faqJsonLd = faqPageJsonLd(faqItems);

  return (
    <Layout>
      <SEO
        title={seo.title}
        description={seo.description}
        canonical={SITE_URL}
        keywords={seo.keywords}
        jsonLd={[pageJsonLd, faqJsonLd] as unknown as Record<string, unknown>[]}
      />

      <section className="homepage-hero page-hero-surface relative flex min-h-[92svh] items-start overflow-hidden grain md:min-h-[100svh] md:items-center lg:min-h-[92svh]">
        <HeroBackground quiet />
        <div className="absolute bottom-0 left-0 top-0 w-[2px] bg-primary/55" />
        <div className="container-wide relative z-10 w-full pb-12 pt-24 sm:pb-14 sm:pt-28 md:pb-20 md:pt-32 lg:pb-20 lg:pt-28">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[8fr_4fr] lg:gap-14">
            <div className="min-w-0 max-w-[52rem]">
              <div className="mb-5 opacity-0 animate-fade-up stagger-1 sm:mb-7">
                <Eyebrow
                  label={isFr ? frCopy.heroEyebrow : "Costly commerce workflows — from scope to operation"}
                  code={isFr ? "VERT / ACCUEIL" : "VERT / HOMEPAGE"}
                  dark
                />
              </div>
              <div className="control-rule mb-6 w-20 opacity-0 animate-fade-up stagger-1 sm:mb-8 sm:w-24" />
              <h1 className="homepage-hero-title heading-hero mb-6 max-w-3xl text-[hsl(var(--sd-fg))] opacity-0 animate-fade-up stagger-2 sm:mb-8 lg:!text-[4.35rem] xl:!text-[4.6rem]">
                {isFr ? (
                  <>
                    <span className="block sm:inline">Corrigez les processus coûteux</span>{" "}
                    <span className="block sm:inline">qui ralentissent vos opérations</span>{" "}
                    <span className="block sm:inline">de commerce.</span>
                  </>
                ) : (
                  <>
                    <span className="block sm:inline">Fix the costly workflows</span>{" "}
                    <span className="block sm:inline">slowing down commerce operations.</span>
                  </>
                )}
              </h1>
              <p className="mb-7 max-w-2xl text-[15.5px] leading-[1.66] text-[hsl(var(--sd-fg)/0.74)] opacity-0 animate-fade-up stagger-3 sm:hidden">
                {heroMobileSubhead}
              </p>
              <p className="text-lead mb-7 hidden max-w-2xl text-[hsl(var(--sd-fg)/0.72)] opacity-0 animate-fade-up stagger-3 sm:block">
                {localize(siteContent.brand.longPositioning, locale)}
              </p>
              <div className="flex flex-col gap-3 opacity-0 animate-fade-up stagger-4 sm:flex-row">
                <PrimaryCtaLink
                  cta={siteContent.ctas.reviewOffer}
                  className="btn-primary btn-hero group w-full justify-center sm:w-auto"
                  dataEventLocation="homepage_hero"
                  dataEventSurface="homepage"
                >
                  {reviewOfferCtaLabel}
                  <ArrowRight className="h-3 w-3" />
                </PrimaryCtaLink>
                <PrimaryCtaLink
                  cta={siteContent.ctas.productionSystems}
                  className="btn-outline btn-hero group w-full justify-center sm:w-auto"
                  dataEventLocation="homepage_hero"
                  dataEventSurface="homepage"
                >
                  {productionSystemsCtaLabel}
                  <ArrowRight className="h-3 w-3" />
                </PrimaryCtaLink>
              </div>
              <ul className="mt-6 hidden max-w-2xl grid-cols-2 gap-x-6 gap-y-3 border-t border-[hsl(var(--sd-fg)/0.12)] pt-5 opacity-0 animate-fade-up stagger-5 md:grid">
                {heroSupportBullets.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-[13.5px] leading-[1.6] text-[hsl(var(--sd-fg)/0.68)]">
                    <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-primary/75" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative mx-auto hidden w-full max-w-md text-[hsl(var(--sd-fg))] opacity-0 animate-fade-up stagger-5 lg:block lg:max-w-none">
              <div className="command-shell-dark p-5 md:p-6">
                <div className="relative z-10">
                  <div className="flex items-center justify-between gap-4 border-b border-[hsl(var(--sd-fg)/0.09)] pb-4">
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/75">
                      {isFr ? "Système opérationnel" : "Production operating system"}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--sd-fg)/0.42)]">
                      {isFr ? "PRÊT / 08" : "READY / 08"}
                    </span>
                  </div>

                  <ServiceFlow nodes={heroFlowNodes} stamp={isFr ? "SYSTÈME / 01" : "SYSTEM / 01"} />

                  <div className="mt-5 border-y border-[hsl(var(--sd-fg)/0.09)]">
                    {(isFr
                      ? [
                          ["Signal", "Cas d’usage mesurable"],
                          ["Contrôle", "Validation humaine définie"],
                          ["Décision", "Chemin de production clair"],
                        ]
                      : [
                          ["Signal", "Measurable use case"],
                          ["Control", "Human review boundary"],
                          ["Decision", "Clear production path"],
                        ]
                    ).map(([label, value]) => (
                      <div key={label} className="status-row first:border-t-0">
                        <span className="status-dot" aria-hidden="true" />
                        <span className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-[hsl(var(--sd-fg)/0.44)]">
                          {label}
                        </span>
                        <span className="text-right text-[12.5px] leading-snug text-[hsl(var(--sd-fg)/0.72)]">
                          {value}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-px border border-[hsl(var(--sd-fg)/0.09)] bg-[hsl(var(--sd-fg)/0.09)]">
                    {consoleMetrics.map((item) => (
                      <div key={item.code} className="bg-[hsl(var(--control-panel))] px-3 py-3">
                        <span className="block font-mono text-[9px] uppercase tracking-[0.14em] text-primary/68">
                          {item.code}
                        </span>
                        <span className="mt-1 block text-[12px] text-[hsl(var(--sd-fg)/0.68)]">
                          {isFr ? item.fr : item.en}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ProofBar items={proofItems} className="dark:hidden" />
      <ProofBar items={proofItems} dark className="hidden dark:block" />

      <Section fullBleed animate className="surface-level-1">
        <div className="container-wide section-padding">
          <div className="relative overflow-hidden border border-primary/30 bg-background p-7 sm:p-10 lg:p-12">
            <div className="absolute bottom-0 left-0 top-0 w-[2px] bg-primary/70" aria-hidden="true" />
            <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-end lg:gap-16">
              <div>
                <Eyebrow
                  label={isFr ? "Examen décisionnel en vedette" : "Featured decision review"}
                  code="R / 01"
                  className="mb-6"
                />
                <h2 className="heading-section max-w-3xl text-foreground">
                  {isFr
                    ? "Vous devez prendre une décision réelle sur le financement, le lancement ou l’expansion d’une initiative d’IA?"
                    : "Facing a real AI funding, launch, or scale decision?"}
                </h2>
                <p className="mt-6 max-w-3xl text-[16px] leading-[1.8] text-muted-foreground">
                  {isFr
                    ? "Vert AI examine une initiative en exploitation et détermine ce que les preuves soutiennent, ce qui demeure une hypothèse, ce qui pourrait échouer et les conditions à remplir avant de poursuivre."
                    : "Vert AI reviews one live initiative and identifies what is supported, what remains assumed, what could fail, and what must become true before proceeding."}
                </p>
                <dl className="mt-8 grid grid-cols-1 gap-px border border-border/45 bg-border/45 sm:grid-cols-3">
                  {[
                    [isFr ? "Périmètre" : "Scope", isFr ? "Une initiative" : "One initiative"],
                    [isFr ? "Délai standard" : "Standard timing", isFr ? "10 jours ouvrables après la préparation des preuves" : "10 business days after Evidence Readiness"],
                    [isFr ? "Honoraires standard" : "Standard fee", isFr ? "9 500 $ CA + taxes applicables" : "C$9,500 + applicable taxes"],
                  ].map(([label, value]) => (
                    <div key={label} className="bg-background p-5">
                      <dt className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-primary/70">{label}</dt>
                      <dd className="mt-3 text-[13.5px] font-semibold leading-snug text-foreground">{value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
              <div className="flex flex-col gap-3">
                <PrimaryCtaLink
                  cta={siteContent.ctas.verificationReview}
                  className="btn-primary group justify-center"
                  dataEventLocation="homepage_featured_review"
                  dataEventSurface="featured_review_section"
                >
                  {isFr ? "Présenter une demande" : "Apply for the Review"}
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </PrimaryCtaLink>
                <Link
                  to="/resources/sample-ai-verification-review"
                  className="btn-outline group justify-center"
                  data-event="secondary_cta_click"
                  data-event-location="homepage_featured_review"
                  data-event-surface="featured_review_section"
                  data-event-target="/resources/sample-ai-verification-review"
                >
                  {isFr ? "Voir l’exemple fictif" : "View the Sample"}
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section>
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.72fr_1.28fr] lg:gap-20">
          <div className="min-w-0">
            <Eyebrow label={isFr ? "Le problème" : "The problem"} code="P / 01" className="mb-6" />
            <h2 className="heading-section max-w-2xl text-foreground">
              {isFr ? frCopy.problemHeadline : "Start with an operating problem whose cost is already visible."}
            </h2>
          </div>
          <div className="min-w-0 border-t border-border/35 pt-8 lg:pt-10">
            <p className="max-w-2xl text-[16px] leading-[1.8] text-muted-foreground">
              {isFr
                ? frCopy.problemBody
                : "Vert AI becomes relevant when important work is slow, manual, or inconsistent—or when a recurring decision remains difficult to execute despite the data already available."}
            </p>
            <ul className="mt-8 divide-y divide-border/45 border-y border-border/45">
              {problemFailures.map((item) => (
                <li
                  key={item.code}
                  className="grid grid-cols-[3.25rem_1fr] items-start gap-x-4 gap-y-2 py-5 sm:grid-cols-[4.75rem_0.38fr_1fr] sm:gap-x-6 sm:gap-y-0"
                >
                  <span className="pt-0.5 font-mono text-[10px] uppercase tracking-[0.16em] text-primary/75">
                    P/{item.code}
                  </span>
                  <h3 className="text-[15px] font-semibold leading-snug text-foreground">{item.label}</h3>
                  <p className="col-start-2 text-[14px] leading-[1.72] text-muted-foreground sm:col-start-3 sm:max-w-xl">
                    {item.body}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <Section fullBleed dark grain>
        <div className="container-wide section-padding relative z-10">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.78fr_1.22fr] lg:gap-16 xl:gap-20">
            <div className="min-w-0">
              <Eyebrow label={isFr ? "Ce que Vert AI construit" : "What Vert AI builds"} code="B / 05" dark className="mb-6" />
              <h2 className="heading-section mb-6 max-w-xl text-[hsl(var(--sd-fg))]">
                {isFr ? frCopy.buildsHeadline : "A redesigned workflow. A reliable system. A measurable result."}
              </h2>
              <p className="max-w-lg text-[15px] leading-[1.8] text-[hsl(var(--sd-fg)/0.68)]">
                {isFr
                  ? frCopy.buildsBody
                  : "Vert AI connects the workflow, data, rules, AI, controls, and measurement in one operating system. The intended result is a capability the team uses and can run with confidence."}
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row lg:flex-col xl:flex-row">
                <Link
                  to="/solutions"
                  className="btn-outline w-full justify-center sm:w-auto"
                  data-event="secondary_cta_click"
                  data-event-location="homepage_builds"
                  data-event-surface="builds_section"
                  data-event-target="/solutions"
                >
                  {isFr ? "Explorer les systèmes d’IA en production" : "Explore production AI systems"}
                  <ArrowRight className="h-3 w-3" />
                </Link>
                <PrimaryCtaLink
                  cta={siteContent.ctas.proof}
                  className="btn-outline w-full justify-center sm:w-auto"
                  dataEventLocation="homepage_builds"
                  dataEventSurface="builds_section"
                >
                  {proofCtaLabel}
                  <ArrowRight className="h-3 w-3" />
                </PrimaryCtaLink>
              </div>
              <div className="mt-10 border-y border-[hsl(var(--sd-fg)/0.1)] py-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/70">
                  {isFr ? "Indicateur d’adéquation" : "Buyer signal"}
                </p>
                <p className="mt-3 max-w-md text-[14px] leading-[1.72] text-[hsl(var(--sd-fg)/0.62)]">
                  {isFr
                    ? "Le bon point de départ est un processus récurrent où l’amélioration de la vitesse, de la qualité, de la priorisation ou de la validation peut être mesurée. Tous les processus ne devraient pas être automatisés."
                    : "The right starting point is a recurring workflow where better speed, quality, prioritization, or review can be measured. Not every workflow should be automated."}
                </p>
              </div>
              <div className="mt-8 border border-[hsl(var(--sd-fg)/0.12)] bg-[hsl(var(--sd-fg)/0.025)] p-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/70">
                  {isFr ? "Mandat réel sélectionné · anonymisé" : "Selected real engagement · anonymized"}
                </p>
                <h3 className="mt-4 text-[17px] font-semibold leading-snug text-[hsl(var(--sd-fg)/0.9)]">
                  {localize(selectedEngagement.title, locale)}
                </h3>
                <p className="mt-3 text-[13.5px] leading-[1.7] text-[hsl(var(--sd-fg)/0.6)]">
                  {localize(selectedEngagement.workflowProblem, locale)}
                </p>
                <p className="mt-3 text-[13.5px] leading-[1.7] text-[hsl(var(--sd-fg)/0.7)]">
                  <span className="font-semibold text-[hsl(var(--sd-fg)/0.86)]">
                    {isFr ? "Capacité livrée : " : "Delivered capability: "}
                  </span>
                  {localize(selectedEngagement.firstPhase, locale)}
                </p>
                <p className="mt-3 text-[12px] leading-[1.65] text-[hsl(var(--sd-fg)/0.48)]">
                  {isFr
                    ? "Aucun résultat chiffré ni nom de client n’est publié sans preuve approuvée."
                    : "No numeric outcome or client identity is published without an approved evidence record."}
                </p>
                <Link to="/work" className="link-underline mt-5 inline-flex text-[13px] text-[hsl(var(--sd-fg)/0.78)]">
                  {proofCtaLabel}
                </Link>
              </div>
            </div>

            <div className="min-w-0">
              <div className="system-card-dark p-5 md:p-6">
                <div className="flex flex-col gap-2 border-b border-[hsl(var(--sd-fg)/0.09)] pb-4 sm:flex-row sm:items-end sm:justify-between sm:pb-5">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/70">
                      {isFr ? "Répertoire des capacités" : "Capability index"}
                    </p>
                    <h3 className="mt-2 text-[18px] font-semibold leading-snug text-[hsl(var(--sd-fg)/0.92)]">
                      {isFr ? frCopy.servicesHeadline : "Four implementation paths after an evidence-backed decision."}
                    </h3>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--sd-fg)/0.5)]">
                    S / {String(services.length).padStart(2, "0")}
                  </span>
                </div>
                <div className="divide-y divide-[hsl(var(--sd-fg)/0.08)]">
                  {services.map((service, index) => (
                    <Link
                      key={service.slug}
                      to={service.href}
                      className="group grid gap-2 py-3.5 transition-colors duration-400 sm:grid-cols-[4.25rem_1fr_auto] sm:items-start sm:gap-3 sm:py-4"
                      data-event="service_page_cta_click"
                      data-event-surface="homepage_capability_index"
                      data-event-target={service.href}
                      data-event-service-slug={service.slug}
                    >
                      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/70">
                        S/{String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[15px] font-semibold leading-snug text-[hsl(var(--sd-fg)/0.88)] transition-colors duration-400 group-hover:text-primary">
                          {localize(service.name, locale)}
                        </span>
                        <span className="mt-1.5 block max-w-2xl text-[13.5px] leading-[1.62] text-[hsl(var(--sd-fg)/0.58)] sm:mt-2 sm:leading-[1.68]">
                          {localize(service.description, locale)}
                        </span>
                      </span>
                      <ArrowRight className="mt-1 h-3.5 w-3.5 text-[hsl(var(--sd-fg)/0.42)] transition-all duration-400 group-hover:translate-x-1 group-hover:text-primary" />
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-8">
                <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/70">
                      {isFr ? "Modèles de valeur" : "Value patterns"}
                    </p>
                    <h3 className="mt-2 text-[18px] font-semibold leading-snug text-[hsl(var(--sd-fg)/0.92)]">
                      {isFr ? frCopy.valueHeadline : "Value shows up when the workflow changes."}
                    </h3>
                  </div>
                  <p className="max-w-sm text-[13.5px] leading-[1.65] text-[hsl(var(--sd-fg)/0.58)]">
                    {isFr
                      ? frCopy.valueBody
                      : "The best use cases are operational, measurable, and close to recurring decisions. Humans stay in control where judgment, risk, or customer impact matters."}
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {valueItems.map((item) => (
                    <Link
                      key={item.code}
                      to={item.href}
                      className="system-card-dark group border-l-2 border-l-transparent p-4 transition-colors duration-400 hover:border-l-primary/60 hover:border-primary/35 sm:p-5"
                    >
                      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/70">{item.code}</span>
                      <h3 className="mt-4 text-[15px] font-semibold leading-snug text-[hsl(var(--sd-fg)/0.9)] transition-colors duration-400 group-hover:text-primary">
                        {localize(item.title, locale)}
                      </h3>
                      <p className="mt-2.5 text-[13.5px] leading-[1.65] text-[hsl(var(--sd-fg)/0.58)] sm:mt-3 sm:leading-[1.7]">
                        {localize(item.description, locale)}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section fullBleed animate>
        <div className="container-wide section-padding bg-surface-1">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.74fr_1.26fr] lg:gap-16 xl:gap-20">
            <div className="min-w-0">
              <Eyebrow label={isFr ? "Du cadrage à la production" : "Scope to production"} code="OS / 08" className="mb-6" />
              <h2 className="heading-section mb-6 max-w-xl text-foreground">
                {isFr ? frCopy.osHeadline : "Choose. Prove. Deploy. Operate."}
              </h2>
              <p className="max-w-lg text-[15px] leading-[1.8] text-muted-foreground">
                {isFr
                  ? frCopy.osBody
                  : "Each phase reduces a specific uncertainty: workflow value, strength of evidence, deployment reliability, or the team’s ability to operate the system."}
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row lg:flex-col xl:flex-row">
                <PrimaryCtaLink
                  className="btn-primary group w-full justify-center sm:w-auto"
                  dataEventLocation="homepage_midpage"
                  dataEventSurface="scope_to_production_section"
                >
                  {primaryCtaLabel}
                  <ArrowRight className="h-3 w-3" />
                </PrimaryCtaLink>
                <Link
                  to="/services"
                  className="btn-outline w-full justify-center sm:w-auto"
                  data-event="secondary_cta_click"
                  data-event-location="homepage_midpage"
                  data-event-surface="scope_to_production_section"
                  data-event-target="/services"
                >
                  {isFr ? "Comparer les types de mandats" : "Compare engagement paths"}
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
              <div className="mt-10 border-t border-border/40 pt-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/75">
                  {isFr ? "Garde-fous de production" : "Production gates"}
                </p>
                <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                  {methodChecks.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-[13.5px] leading-[1.6] text-muted-foreground">
                      <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-primary/75" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="min-w-0">
              <div className="grid grid-cols-1 gap-7 lg:grid-cols-[0.72fr_1.28fr] lg:gap-10">
                <div className="system-card p-5 sm:p-6 md:p-7">
                  <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/75">
                    {isFr ? "Méthode" : "Delivery method"}
                  </p>
                  <h3 className="mt-5 text-[18px] font-semibold leading-snug text-foreground">
                    {isFr ? frCopy.processHeadline : "A short path from diagnostic to operating system."}
                  </h3>
                  <p className="mt-4 text-[14px] leading-[1.75] text-muted-foreground">
                    {isFr
                      ? "Le travail commence par le problème opérationnel, pas par le modèle. Chaque étape réduit le risque avant la production."
                      : "The work starts with the operating problem, not the model. Each step reduces delivery risk before production."}
                  </p>
                </div>
                <ul className="border-t border-border/35">
                  {processSteps.map((step, index) => (
                    <ProcessStep
                      key={localize(step.title, locale)}
                      code={`P/${String(index + 1).padStart(2, "0")}`}
                      title={localize(step.title, locale)}
                      description={localize(step.description, locale)}
                      marker={step.marker ? (isFr ? "HUMAIN /" : step.marker) : undefined}
                    />
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section fullBleed dark grain>
        <div className="container-wide section-padding relative z-10">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.78fr_1.22fr] lg:gap-16 xl:gap-20">
            <div className="min-w-0">
              <Eyebrow label={isFr ? "Confiance" : "Trust and governance"} code="T / 08" dark className="mb-6" />
              <h2 className="heading-section mb-6 max-w-xl text-[hsl(var(--sd-fg))]">
                {isFr ? frCopy.trustHeadline : "Production AI needs more than a model."}
              </h2>
              <p className="max-w-lg text-[15px] leading-[1.8] text-[hsl(var(--sd-fg)/0.68)]">
                {isFr
                  ? frCopy.trustBody
                  : "The system has to be observable, governed, measurable, and usable by the teams who depend on it. Humans stay in control where judgment, risk, or customer impact matters."}
              </p>
              <PrimaryCtaLink
                cta={siteContent.ctas.secondary}
                className="btn-outline group mt-8 w-full justify-center sm:w-auto"
                dataEventLocation="homepage_trust"
                dataEventSurface="trust_section"
              >
                {secondaryCtaLabel}
                <ArrowRight className="h-3 w-3" />
              </PrimaryCtaLink>
            </div>

            <div className="min-w-0">
              <div className="grid grid-cols-2 gap-px border border-[hsl(var(--sd-fg)/0.1)] bg-[hsl(var(--sd-fg)/0.1)]">
                {trustItems.map((item, index) => (
                  <div key={localize(item, locale)} className="bg-[hsl(var(--control-panel))] p-4 sm:p-5 md:p-6">
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/72">
                      T/{String(index + 1).padStart(2, "0")}
                    </span>
                    <p className="mt-3 text-[13.5px] leading-[1.62] text-[hsl(var(--sd-fg)/0.74)] sm:mt-4 sm:text-[14px] sm:leading-[1.68]">
                      {localize(item, locale)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="system-card-dark mt-8 p-6 md:p-7">
                <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.34fr_0.66fr]">
                  <div>
                    <Eyebrow label={isFr ? "Fondateur" : "Founder credibility"} code="F / 01" dark className="mb-6" />
                    <div className="control-rule mb-6 w-20" />
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--sd-fg)/0.58)]">
                      Guillaume Belisle / Vert AI
                    </p>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-[22px] font-semibold leading-tight text-[hsl(var(--sd-fg))] md:text-[26px]">
                      {isFr ? frCopy.founderHeadline : "Founder-led production experience."}
                    </h3>
                    <p className="mt-5 max-w-2xl text-[15px] leading-[1.8] text-[hsl(var(--sd-fg)/0.66)]">
                      {isFr
                        ? frCopy.founderBody
                        : "Guillaume leads scoping and key technical decisions. His professional experience includes designing and shipping production AI, ML, and data systems in environments including Louis Vuitton, Canada Goose, Lowe’s, and Home Hardware. These organizations are founder-experience references, not presented as Vert AI clients."}
                    </p>
                    <div className="mt-7 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
                      <Link
                        to="/about"
                        className="link-underline inline-flex items-center gap-2 text-[13.5px] font-medium text-[hsl(var(--sd-fg)/0.86)] transition-colors duration-400 hover:text-primary"
                        data-event="secondary_cta_click"
                        data-event-location="homepage_founder"
                        data-event-surface="founder_section"
                        data-event-target="/about"
                      >
                        {isFr ? "Voir la livraison dirigée par le fondateur" : "Review founder-led delivery"}
                        <ArrowRight className="h-3 w-3" />
                      </Link>
                      <Link
                        to="/insights"
                        className="link-underline inline-flex items-center gap-2 text-[13.5px] font-medium text-[hsl(var(--sd-fg)/0.86)] transition-colors duration-400 hover:text-primary"
                        data-event="insights_cta_click"
                        data-event-location="homepage_founder"
                        data-event-surface="founder_section"
                        data-event-target="/insights"
                      >
                        {isFr ? "Lire l’analyse de mise en œuvre" : "Read implementation analysis"}
                        <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section fullBleed animate>
        <div className="container-wide section-padding bg-surface-1">
          <div className="mb-10 grid grid-cols-1 gap-8 lg:grid-cols-[0.8fr_1fr] lg:items-end lg:gap-16">
            <div className="min-w-0">
              <Eyebrow label={isFr ? "Opérateurs" : "Who it is for"} code="I / 05" className="mb-6" />
              <h2 className="heading-section mb-5 max-w-2xl text-foreground">
                {isFr ? frCopy.industriesHeadline : "Built for operators where workflow quality matters."}
              </h2>
              <p className="max-w-2xl text-[15px] leading-[1.8] text-muted-foreground">
                {isFr
                  ? frCopy.industriesBody
                  : "The best engagements sit inside companies where data quality, exceptions, recurring decisions, and operational throughput have real cost."}
              </p>
            </div>
            <div className="flex lg:justify-end">
              <Link
                to="/industries"
                className="btn-outline w-full justify-center sm:w-auto"
                data-event="secondary_cta_click"
                data-event-location="homepage_industries"
                data-event-surface="industries_section"
                data-event-target="/industries"
              >
                {isFr ? "Examiner l’adéquation par secteur" : "Review industry fit"}
                <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {industries.map((industry, index) => (
              <li key={industry.slug}>
                <IndustryCard
                  code={`I/${String(index + 1).padStart(2, "0")}`}
                  title={localize(industry.label, locale)}
                  description={
                    isFr
                      ? "Processus opérationnels, données et décisions récurrentes où un système d’IA doit être fiable."
                      : "Operational workflows, data, and recurring decisions where an AI system needs to be trusted."
                  }
                  href={industry.href}
                  ctaLabel={isFr ? "Examiner l’adéquation" : "Review industry fit"}
                />
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <Section narrow padding="sm">
        <Eyebrow label={isFr ? "Adéquation" : "Engagement fit"} code="FIT / 02" className="mb-6" />
        <h2 className="heading-section mb-5 max-w-2xl text-foreground">
          {isFr ? "Un bon mandat commence par un problème opérationnel précis." : "A strong engagement starts with a specific operating problem."}
        </h2>
        <p className="mb-10 max-w-2xl text-[15px] leading-[1.8] text-muted-foreground">
          {isFr
            ? "Ces limites permettent de concentrer le cadrage sur les occasions qui peuvent devenir des capacités exploitées et mesurables."
            : "These boundaries keep scoping focused on opportunities that can become adopted, measurable operating capabilities."}
        </p>
        <div className="grid grid-cols-1 gap-px bg-border/40 md:grid-cols-2">
          {[
            { label: isFr ? "Bonne adéquation" : "Good fit", items: goodFit, tone: "text-primary" },
            { label: isFr ? "Ne convient pas" : "Not a fit", items: notFit, tone: "text-muted-foreground" },
          ].map((group) => (
            <div key={group.label} className="bg-background p-6 sm:p-8">
              <h3 className={`font-mono text-[10px] uppercase tracking-[0.16em] ${group.tone}`}>{group.label}</h3>
              <ul className="mt-5 space-y-4">
                {group.items.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-[14px] leading-[1.7] text-muted-foreground">
                    <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-primary/70" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <PrimaryCtaLink cta={siteContent.ctas.proof} className="btn-outline mt-8 inline-flex">
          {proofCtaLabel}
          <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </PrimaryCtaLink>
      </Section>

      <Section narrow padding="sm">
        <Eyebrow label="FAQ" code={`FAQ / ${String(faqItems.length).padStart(2, "0")}`} className="mb-6" />
        <h2 className="heading-section mb-8 text-foreground sm:mb-12">
          {isFr ? "Questions fréquentes." : "Direct answers for serious buyers."}
        </h2>
        <FAQAccordion items={faqItems} />
      </Section>

      <section className="relative overflow-hidden section-dark grain">
        <div className="shimmer-line" />
        <div className="container-wide section-padding relative z-10">
          <div className="control-panel mx-auto max-w-4xl p-7 sm:p-10 md:p-14">
            <Eyebrow label={isFr ? "Prochaine décision" : "Next decision"} code="CTA / FINAL" dark className="mb-10" />
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.5fr_1fr] lg:items-end">
              <div className="min-w-0">
                <h2 className="heading-editorial mb-6 max-w-2xl text-[hsl(var(--sd-fg))]">
                  {isFr ? frCopy.finalHeadline : "Bring the costly workflow your team should not have to keep carrying."}
                </h2>
                <p className="max-w-xl text-[15px] leading-[1.8] text-[hsl(var(--sd-fg)/0.66)]">
                  {isFr
                    ? frCopy.finalBody
                    : "Bring a workflow, a data problem, or a recurring decision. We will map the value, risks, ownership, and path to production, including whether not to build yet."}
                </p>
              </div>
              <div className="flex flex-col gap-3 lg:items-end">
                <PrimaryCtaLink
                  className="btn-primary group w-full justify-center sm:w-auto"
                  dataEventLocation="homepage_final"
                  dataEventSurface="final_cta"
                >
                  {primaryCtaLabel}
                  <ArrowRight className="h-3 w-3" />
                </PrimaryCtaLink>
                <PrimaryCtaLink
                  cta={siteContent.ctas.secondary}
                  className="btn-outline group w-full justify-center sm:w-auto"
                  dataEventLocation="homepage_final"
                  dataEventSurface="final_cta"
                >
                  {secondaryCtaLabel}
                  <ArrowRight className="h-3 w-3" />
                </PrimaryCtaLink>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
